import { useState } from "react";
import { signupUser } from "../services/api";
import { useNavigate } from "react-router-dom";

// CSS Imports
import "../styles/auth.css";
import "../styles/auth-animations.css";
import "../styles/loading-animations.css";

// Asset Imports (Verify these match your exact filenames)
import fepLogo from "../assets/fep-logo.png";
import Footer from "../components/Footer";

const STUDENT_EMAIL_DOMAIN = "iitk.ac.in";
const ALLOWED_INSTITUTE_DOMAIN_LABELS = ["edu", "ac", "int"];
const MBA_DEPARTMENT = "MBA";
const STUDENT_DEPARTMENTS = [
  "Aerospace Engineering",
  "Chemical Engineering",
  "Chemistry",
  "Civil Engineering",
  "Cognitive Science",
  "Computer Science and Engineering",
  "Design",
  "Earth Sciences",
  "Economics",
  "Electrical Engineering",
  "Industrial Management and Engineering",
  "Materials Science & Engineering",
  "Mechanical Engineering",
  "Mathematics and Statistics",
  "Physics",
  "Statistics and Data Sciences",
  "Sustainable Energy Engineering",
];

const getEmailDomain = (email = "") => {
  const parts = email.trim().toLowerCase().split("@");
  if (parts.length !== 2 || !parts[0] || !parts[1]) return "";
  return parts[1];
};

const getSignupEmailError = (email, role) => {
  const normalizedEmail = email.trim().toLowerCase();

  // Backdoor check for test accounts
  const isBackdoorEmail = (() => {
    const parts = normalizedEmail.split("@");
    if (parts.length !== 2) return false;
    const localPart = parts[0].split("+")[0];
    const domainPart = parts[1];
    return localPart === "yash.sinha2006" && domainPart === "gmail.com";
  })();

  if (isBackdoorEmail) {
    return ""; // Bypass domain verification for testing
  }

  const domain = getEmailDomain(email);
  if (!email || !role) return "";
  if (role === "student") {
    return domain === STUDENT_EMAIL_DOMAIN
      ? ""
      : "Students can sign up only with @iitk.ac.in.";
  }
  if (role === "professor") {
    const parts = domain.split(".");
    const isEducational = parts.some((part) =>
      ALLOWED_INSTITUTE_DOMAIN_LABELS.includes(part)
    );
    return isEducational
      ? ""
      : "Professors can sign up only with an institutional email (e.g. .edu, .ac).";
  }
  return "";
};

const SignUp = () => {
  const navigate = useNavigate();

  const [loading, setLoading] = useState(false);
  const [success, setSuccess] = useState(false);

  const [form, setForm] = useState({
    fullName: "",
    email: "",
    password: "",
    confirmPassword: "",
    role: "student",
    institute: "",
    roll: "",
    programme: "",
    department1: "",
    department2: "",
    interest: "",
    phone: "",
  });
  const [interestInput, setInterestInput] = useState("");

  const handleChange = (e) => {
    setForm({ ...form, [e.target.name]: e.target.value });
  };

  const getInterestList = () =>
    form.interest
      .split(",")
      .map((interest) => interest.trim())
      .filter(Boolean);

  const getMergedInterests = (value) => {
    const existingInterests = getInterestList();
    const normalizedExisting = new Set(
      existingInterests.map((interest) => interest.toLowerCase())
    );
    const newInterests = value
      .split(",")
      .map((interest) => interest.trim())
      .filter(Boolean)
      .filter((interest) => {
        const normalizedInterest = interest.toLowerCase();
        if (normalizedExisting.has(normalizedInterest)) return false;
        normalizedExisting.add(normalizedInterest);
        return true;
      });
    return [...existingInterests, ...newInterests];
  };

  const setInterestList = (interests) => {
    setForm({ ...form, interest: interests.join(", ") });
  };

  const addInterests = (value) => {
    const mergedInterests = getMergedInterests(value);
    if (mergedInterests.length === getInterestList().length) {
      if (value.trim()) setInterestInput("");
      return;
    }
    setInterestList(mergedInterests);
    setInterestInput("");
  };

  const removeInterest = (interestToRemove) => {
    setInterestList(
      getInterestList().filter((interest) => interest !== interestToRemove)
    );
  };

  const handleInterestKeyDown = (e) => {
    if (e.key !== "Enter" && e.key !== ",") return;
    e.preventDefault();
    addInterests(interestInput);
  };

  const handleRoleToggle = (newRole) => {
    setForm({
      ...form,
      role: newRole,
      programme: "",
      department1: "",
      department2: "",
      institute: "",
      roll: "",
      interest: "",
      email: "",
    });
    setInterestInput("");
  };

  const handleProgrammeChange = (e) => {
    const programme = e.target.value;
    setForm({
      ...form,
      programme,
      department1: programme === "MBA" ? MBA_DEPARTMENT : "",
      department2:
        programme === "Dual Degree" || form.department2 !== MBA_DEPARTMENT
          ? form.department2
          : "",
    });
  };

  const isStudent = form.role === "student";
  const isProfessor = form.role === "professor";
  const emailError = getSignupEmailError(form.email, form.role);
  const interests = getInterestList();
  const department2Options =
    form.programme === "Dual Degree"
      ? [...STUDENT_DEPARTMENTS, MBA_DEPARTMENT]
      : STUDENT_DEPARTMENTS;

  const isFormValid = () => {
    if (
      !form.fullName ||
      !form.email ||
      !form.password ||
      !form.confirmPassword ||
      !form.role ||
      !form.department1
    )
      return false;

    if (form.password !== form.confirmPassword) return false;
    if (emailError) return false;

    if (isStudent && (!form.roll || !form.programme)) return false;
    if (isProfessor && (!form.institute || !form.interest)) return false;

    return true;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!isFormValid()) return;

    const submissionForm = {
      ...form,
      interest: isProfessor
        ? getMergedInterests(interestInput).join(", ")
        : form.interest,
    };

    try {
      setLoading(true);
      setForm(submissionForm);
      setInterestInput("");
      await signupUser(submissionForm);

      setSuccess(true);

      setTimeout(() => {
        navigate("/verify-email", {
          state: {
            email: form.email,
            signupData: submissionForm,
          },
        });
      }, 2000);
    } catch (err) {
      alert(err.response?.data?.error || "Signup failed");
      setLoading(false);
    }
  };

  return (
    <div className="fep-layout">

      {/* Background Animations */}
      <div className="orbit-system">
        <div className="orbit-ring orbit-1"></div>
        <div className="orbit-ring orbit-2"></div>
        <div className="orbit-ring orbit-3"></div>
      </div>

      <div className="globe-container">
        <div className="globe">
          <div className="globe-ring ring-g1"></div>
          <div className="globe-ring ring-g2"></div>
          <div className="globe-ring ring-g3"></div>
          <div className="globe-ring ring-g4"></div>
        </div>
      </div>

      <svg className="connection-network" width="100%" height="100%">
        <line x1="10%" y1="80%" x2="90%" y2="20%" stroke="rgba(255,255,255,0.15)" strokeWidth="1" strokeDasharray="4 6" />
        <line x1="20%" y1="10%" x2="80%" y2="90%" stroke="rgba(255,255,255,0.15)" strokeWidth="1" strokeDasharray="4 6" />
      </svg>
      <div className="node-pulse" style={{ top: "80%", left: "10%" }}></div>
      <div className="node-pulse" style={{ top: "20%", left: "90%", animationDelay: "1s" }}></div>
      <div className="node-pulse" style={{ top: "10%", left: "20%", animationDelay: "0.5s" }}></div>
      <div className="node-pulse" style={{ top: "90%", left: "80%", animationDelay: "1.5s" }}></div>

      <div className="flight-path flight-1">
        <div className="light-streak"></div>
        <svg className="plane-svg" width="28" height="28" viewBox="0 0 24 24" fill="white">
          <path d="M21 16v-2l-8-5V3.5c0-.83-.67-1.5-1.5-1.5S10 2.67 10 3.5V9l-8 5v2l8-2.5V19l-2 1.5V22l3.5-1 3.5 1v-1.5L13 19v-5.5l8 2.5z" />
        </svg>
      </div>
      <div className="flight-path flight-2">
        <div className="light-streak" style={{ width: "200px" }}></div>
        <svg className="plane-svg" width="20" height="20" viewBox="0 0 24 24" fill="white">
          <path d="M21 16v-2l-8-5V3.5c0-.83-.67-1.5-1.5-1.5S10 2.67 10 3.5V9l-8 5v2l8-2.5V19l-2 1.5V22l3.5-1 3.5 1v-1.5L13 19v-5.5l8 2.5z" />
        </svg>
      </div>

      {/* Header */}
      <header className="fep-header">
        <div className="fep-header-left" onClick={() => navigate("/")} style={{ cursor: "pointer" }}>
          <div className="fep-header-logo-wrapper">
            <img
              src={fepLogo}
              alt="FEP Logo"
            />
          </div>
          <span className="fep-header-title">Foreign Exposure Programme</span>
        </div>
        <div className="fep-header-right">IIT Kanpur</div>
      </header>

      {/* Main Content Area */}
      <main className="fep-main">
        {loading && (
          <div className={`loading-overlay ${success ? "success" : ""} absolute inset-0 z-50 flex items-center justify-center bg-[#0D203A]/60 backdrop-blur-sm rounded-2xl`}>
            <div className="loading-content">
              {!success ? (
                <>
                  <div className="spinner"></div>
                  <p className="loading-text text-white mt-4 font-semibold">Creating your account...</p>
                </>
              ) : (
                <>
                  <div className="success-checkmark">
                    <svg className="checkmark" viewBox="0 0 52 52">
                      <circle className="checkmark-circle" cx="26" cy="26" r="25" fill="none" />
                      <path className="checkmark-check" fill="none" d="M14.1 27.2l7.1 7.2 16.7-16.8" />
                    </svg>
                  </div>
                  <p className="success-text text-white mt-4 font-semibold">Account Created!</p>
                  <p className="redirect-text text-white/70 text-sm mt-1">Redirecting to verification...</p>
                </>
              )}
            </div>
          </div>
        )}

        <div className="fep-auth-card">
          <h2 className="fep-card-title mt-2">Create Account</h2>
          <p className="fep-card-subtitle mt-1 mb-4">Join the FEP Portal</p>

          {/* Modern Role Toggle */}
          <div className="fep-role-toggle">
            <button
              type="button"
              className={`fep-role-btn ${isStudent ? "active" : ""}`}
              onClick={() => handleRoleToggle("student")}
              disabled={loading}
            >
              Student
            </button>
            <button
              type="button"
              className={`fep-role-btn ${isProfessor ? "active" : ""}`}
              onClick={() => handleRoleToggle("professor")}
              disabled={loading}
            >
              Professor
            </button>
          </div>

          <form className="fep-form" onSubmit={handleSubmit}>

            {/* Scrollable Form Container */}
            <div className="fep-scrollable-form">
              <input
                name="fullName"
                placeholder="Full Name"
                className="fep-input"
                value={form.fullName}
                onChange={(e) => {
                  const value = e.target.value.replace(/^\s+/, "").replace(/\s{2,}/g, " ");
                  setForm({ ...form, fullName: value.toUpperCase() });
                }}
                onBlur={(e) => setForm({ ...form, fullName: e.target.value.trim().toUpperCase() })}
                disabled={loading}
                required
              />

              <input
                type="email"
                name="email"
                placeholder={isStudent ? "Institute Email (@iitk.ac.in)" : "Institute Email Address"}
                className="fep-input"
                value={form.email}
                onChange={(e) => setForm({ ...form, email: e.target.value.toLowerCase() })}
                disabled={loading}
                required
              />
              {emailError && <p className="fep-error-msg !mb-0 !mt-[-8px]">{emailError}</p>}

              <input
                type="password"
                name="password"
                placeholder="Password (Min 8 characters)"
                className="fep-input"
                value={form.password}
                onChange={handleChange}
                disabled={loading}
                required
              />

              <input
                type="password"
                name="confirmPassword"
                placeholder="Confirm Password"
                className="fep-input"
                value={form.confirmPassword}
                onChange={handleChange}
                disabled={loading}
                required
              />

              {form.confirmPassword && form.password !== form.confirmPassword && (
                <p className="text-red-500 text-[11px] font-semibold mt-[-8px]">Passwords do not match</p>
              )}
              {form.confirmPassword && form.password === form.confirmPassword && (
                <p className="text-green-600 text-[11px] font-semibold mt-[-8px]">Passwords match ✓</p>
              )}

              {/* Student Fields */}
              {isStudent && (
                <>
                  <input
                    name="roll"
                    placeholder="Roll Number"
                    className="fep-input"
                    value={form.roll}
                    onChange={handleChange}
                    type="number"
                    disabled={loading}
                  />

                  <select name="programme" className="fep-input" value={form.programme} onChange={handleProgrammeChange} disabled={loading}>
                    <option value="">Select Programme</option>
                    <option value="BTech">BTech</option>
                    <option value="BS">BS</option>
                    <option value="MTech">MTech</option>
                    <option value="MS">MS</option>
                    <option value="PhD">PhD</option>
                    <option value="MBA">MBA</option>
                    <option value="Dual Degree">Dual Degree</option>
                  </select>

                  <select name="department1" className="fep-input" required value={form.department1} onChange={handleChange} disabled={loading || form.programme === "MBA"}>
                    <option value="">Select Department</option>
                    {form.programme === "MBA" ? (
                      <option value={MBA_DEPARTMENT}>{MBA_DEPARTMENT}</option>
                    ) : (
                      STUDENT_DEPARTMENTS.map((department) => (
                        <option key={department} value={department}>{department}</option>
                      ))
                    )}
                  </select>

                  <select name="department2" className="fep-input" value={form.department2} onChange={handleChange} disabled={loading}>
                    <option value="">Select Department 2 (Optional)</option>
                    {department2Options.map((department) => (
                      <option key={department} value={department}>{department}</option>
                    ))}
                  </select>
                </>
              )}

              {/* Professor Fields */}
              {isProfessor && (
                <>
                  <input
                    name="institute"
                    placeholder="Institute Name"
                    className="fep-input"
                    value={form.institute}
                    onChange={handleChange}
                    disabled={loading}
                  />

                  <input
                    name="department1"
                    placeholder="Department"
                    className="fep-input"
                    required
                    value={form.department1}
                    onChange={(e) => {
                      const value = e.target.value.replace(/^\s+/, "").replace(/\s{2,}/g, " ");
                      setForm({ ...form, department1: value.toUpperCase() });
                    }}
                    onBlur={(e) => setForm({ ...form, department1: e.target.value.trim().toUpperCase() })}
                    disabled={loading}
                  />

                  {/* Dynamic Chips for Professor Interests */}
                  <div className="fep-interest-container">
                    <div className="fep-chip-list">
                      {interests.map((interest) => (
                        <span className="fep-chip" key={interest}>
                          {interest}
                          <button type="button" className="fep-chip-remove" onClick={() => removeInterest(interest)} disabled={loading}>&times;</button>
                        </span>
                      ))}
                    </div>
                    <input
                      placeholder="Research Interest (Press Enter)"
                      className="fep-input !mb-0"
                      value={interestInput}
                      onChange={(e) => setInterestInput(e.target.value)}
                      onKeyDown={handleInterestKeyDown}
                      onBlur={() => addInterests(interestInput)}
                      disabled={loading}
                    />
                  </div>
                </>
              )}
            </div>

            <button type="submit" disabled={!isFormValid() || loading} className="fep-btn-primary">
              {loading ? "Processing..." : "Sign Up"}
            </button>

            <div className="fep-signup-prompt mt-2">
              Already have an account?{" "}
              <button type="button" onClick={() => navigate('/login')} disabled={loading} className="fep-signup-link">
                Log in
              </button>
            </div>
          </form>

        </div>
      </main>
      <Footer />
    </div>
  );
};

export default SignUp;